import React, { useState, useEffect, useCallback } from "react";
import { _NDNFT } from "../../../abi.js";
import { UUPSRewardManager } from "../../../abi/UUPSRewardManager.js";
import { ethers } from "ethers";
import { toast } from "react-toastify";
import {
    useAppKitAccount,
    useAppKitProvider
} from "@reown/appkit/react";

/**
 * FLAG BIT ORDER — MUST MATCH CONTRACT
 */
const FLAGS = [
    "extMintHook",        // 0
    "bonusERC721",        // 1
    "rmEnabled",          // 2 (Disable RM)
    "rmEnabledStk",       // 3
    "autoDelegation",     // 4
    "randomMinting",      // 5
    "softStaking",        // 6
    "getExternalPrice",   // 7
    "preSaleReduction",  // 8
    "mintDisabled",       // 9
    "splitRoyalty",       // 10
    "onChainTraits"       // 11
];

/**
 * AdvancedSettings component.
 *
 * Modal for configuring advanced NFT contract options.
 * Provides UI for 12+ flags and settings including:
 * - External mint hooks
 * - Bonus ERC721
 * - RewardManager enable/disable
 * - Auto delegation, random minting, soft staking
 * - External price oracle
 * - Whitelist discounts
 * - Split royalties
 * - Mint reflections
 * - Minters lifetime cut
 *
 * Loads current contract configuration and allows committing changes.
 * Uses flag bits (0-11) matching contract implementation.
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - NFT collection/item with NFTProxyAddr, contractAddress
 * @param {Function} props.onCommit - Callback after successful commit
 * @param {Function} props.onClose - Callback to close modal
 * @returns {React.ReactElement} Advanced settings form
 */
const AdvancedSettings = ({ item, onCommit, onClose }) => {
    const { walletProvider } = useAppKitProvider("eip155");
    const { isConnected } = useAppKitAccount();

    const [loading, setLoading] = useState(true);
    const [configAddr, setConfigAddr] = useState("");
    const [configs, setConfigs] = useState({});
    const [rmc, setRmc] = useState(null);

    const [form, setForm] = useState({
        extMintHook: false,
        emh_addr: "",

        bonusERC721: false,
        be721_addr: "",

        rmEnabled: false,
        rm_address: "",

        rmEnabledStk: false,
        stake_address: "",

        autoDelegation: false,
        randomMinting: false,
        softStaking: false,

        getExternalPrice: false,
        ep_addr: "",
        ep_func: "",

        preSaleReduction: false,
        wl_dcpct: "",

        mintDisabled: false,

        splitRoyalty: false,
        sr_bips: "",

        onChainTraits: false,

        mintReflection: false,
        ref_bips: "",

        mintersCut: false,
        mlt_bips: ""
    });

    const signer = useCallback(() => {
        if (!walletProvider) return null;
        return new ethers.providers.Web3Provider(walletProvider).getSigner();
    }, [walletProvider]);

    /* ---------------- LOAD CONTRACT STATE ---------------- */

    useEffect(() => {
        const load = async () => {
            if (!isConnected || !item) return;

            try {
                const s = signer();
                const ctr = new ethers.Contract(
                    item.NFTProxyAddr ?? item.contractAddress,
                    _NDNFT,
                    s
                );

                const cfgAddr = await ctr.NFTConfiguratorAddr();
                const flags = await ctr.fe();
                const cfg = await ctr.cfg();

                setConfigAddr(cfgAddr);
                setConfigs(cfg);

                if (cfg?.RMAddress) {
                    setRmc(new ethers.Contract(cfg.RMAddress, UUPSRewardManager, s));
                }

                setForm(f => ({
                    ...f,
                    ...flags,
                    rm_address: cfg?.RMAddress || ""
                }));
            } catch (e) {
                console.error(e);
                toast.error("Failed to load advanced config");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [item, isConnected, signer]);

    /* ---------------- FORM ---------------- */

    const onChange = e => {
        const { name, type, checked, value } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    /* ---------------- COMMIT ---------------- */

    const handleCommit = async () => {
        const s = signer();
        if (!s) return toast.error("Wallet not connected");

        let flagsUint = 0;
        let mul = 1;
        const addArgs = [];

        FLAGS.forEach(f => {
            if (form[f]) flagsUint += mul;
            mul *= 2;
        });

        if (form.extMintHook)
            addArgs.push(["extMintHook", form.emh_addr, 0, ""]);

        if (form.bonusERC721)
            addArgs.push(["bonusERC721", form.be721_addr, 0, ""]);

        if (form.getExternalPrice)
            addArgs.push([
                "externalPrice",
                form.ep_addr,
                0,
                form.ep_func || "getPrice()"
            ]);

        if (form.preSaleReduction)
            addArgs.push([
                "WLDiscount",
                ethers.constants.AddressZero,
                parseInt(form.wl_dcpct, 10),
                ""
            ]);

        if (form.splitRoyalty)
            addArgs.push([
                "splitRoyalty",
                ethers.constants.AddressZero,
                parseInt(form.sr_bips, 10),
                ""
            ]);

        if (form.mintReflection)
            addArgs.push([
                "rPoolPercent",
                ethers.constants.AddressZero,
                parseInt(form.ref_bips, 10),
                ""
            ]);

        try {
            const cctr = new ethers.Contract(
                configAddr,
                [
                    "function advConfig(address ctr,uint256 flags,tuple(string,address,uint256,string)[])",
                    "function Address_RewardManager(address ctr,address rm,bool immed)"
                ],
                s
            );

            await cctr.advConfig(item.NFTProxyAddr, flagsUint, addArgs, {
                gasLimit: 8_000_000
            });

            if (form.mintersCut && rmc) {
                await rmc.setMinterCut(parseInt(form.mlt_bips, 10));
            }

            toast.success("Advanced config committed");
            onCommit?.();
        } catch (e) {
            toast.error(e.reason || e.message);
        }
    };

    if (loading) return <div>Loading advanced settings…</div>;

    /* ---------------- UI ---------------- */

    return (
        <div style={{ minWidth: "98%" }}>
            <center>
                <b>Advanced config options — use with caution</b>
                <br />
                <a
                    className="text-light"
                    href="https://f.viridissima.es/d/12-guide-to-nft-deployer-advanced-options"
                    target="_blank"
                    rel="noreferrer"
                >
                    Instructions
                </a>
            </center>

            <br />

            {/* FLAG 0 */}
            <input type="checkbox" name="extMintHook" checked={form.extMintHook} onChange={onChange} />
            Enable Mint Hook
            <br />
            {form.extMintHook && (
                <input className="cbItem input-purple w-75" name="emh_addr" placeholder="ExtMintHook address" value={form.emh_addr} onChange={onChange} />
            )}

            <br />

            {/* FLAG 1 */}
            <input type="checkbox" name="bonusERC721" checked={form.bonusERC721} onChange={onChange} />
            Enable Bonus ERC721
            <br />
            {form.bonusERC721 && (
                <input className="cbItem input-purple w-75" name="be721_addr" placeholder="Bonus ERC721 address" value={form.be721_addr} onChange={onChange} />
            )}

            <br />

            {/* FLAG 2 */}
            <input type="checkbox" name="rmEnabled" checked={form.rmEnabled} onChange={onChange} />
            Disable RewardManager
            <br />
            {!form.rmEnabled && (
                <input className="cbItem input-purple w-75" name="rm_address" placeholder="RewardManager address" value={form.rm_address} onChange={onChange} />
            )}

            <br />

            {/* FLAG 3 */}
            <input type="checkbox" name="rmEnabledStk" checked={form.rmEnabledStk} onChange={onChange} />
            Enable RM While Staking
            <br />

            {/* FLAG 4 */}
            <input type="checkbox" name="autoDelegation" checked={form.autoDelegation} onChange={onChange} />
            Enable Auto Delegation
            <br />

            {/* FLAG 5 */}
            <input type="checkbox" name="randomMinting" checked={form.randomMinting} onChange={onChange} />
            Enable Random Minting
            <br />

            {/* FLAG 6 */}
            <input type="checkbox" name="softStaking" checked={form.softStaking} onChange={onChange} />
            Enable Soft Staking
            <br />

            {/* FLAG 7 */}
            <input type="checkbox" name="getExternalPrice" checked={form.getExternalPrice} onChange={onChange} />
            Enable External Price
            <br />
            {form.getExternalPrice && (
                <>
                    <input className="cbItem input-purple w-75" name="ep_addr" placeholder="External price contract" value={form.ep_addr} onChange={onChange} />
                    <input className="cbItem input-purple w-75" name="ep_func" placeholder="getPrice()" value={form.ep_func} onChange={onChange} />
                </>
            )}

            <br />

            {/* FLAG 8 */}
            <input type="checkbox" name="preSaleReduction" checked={form.preSaleReduction} onChange={onChange} />
            Enable Whitelist Discount
            <br />
            {form.preSaleReduction && (
                <input type="number" className="cbItem input-purple w-75" name="wl_dcpct" placeholder="%" value={form.wl_dcpct} onChange={onChange} />
            )}

            <br />

            {/* FLAG 9 */}
            <input type="checkbox" name="mintDisabled" checked={form.mintDisabled} onChange={onChange} />
            Disable Minting
            <br />

            {/* FLAG 10 */}
            <input type="checkbox" name="splitRoyalty" checked={form.splitRoyalty} onChange={onChange} />
            Enable Split Royalty
            <br />
            {form.splitRoyalty && (
                <input type="number" className="cbItem input-purple w-75" name="sr_bips" placeholder="bips" value={form.sr_bips} onChange={onChange} />
            )}

            <br />

            {/* FLAG 11 */}
            <input type="checkbox" name="onChainTraits" checked={form.onChainTraits} onChange={onChange} />
            Enable On-Chain Traits
            <br />

            <hr />

            {/* NON-FLAG OPTIONS */}
            <input type="checkbox" name="mintReflection" checked={form.mintReflection} onChange={onChange} />
            Enable Mint Reflection
            <br />
            {form.mintReflection && (
                <input type="number" className="cbItem input-purple w-75" name="ref_bips" placeholder="%" value={form.ref_bips} onChange={onChange} />
            )}

            <br />

            <input type="checkbox" name="mintersCut" checked={form.mintersCut} onChange={onChange} />
            Minters Lifetime Cut
            <br />
            {form.mintersCut && (
                <input type="number" className="cbItem input-purple w-75" name="mlt_bips" placeholder="bips" value={form.mlt_bips} onChange={onChange} />
            )}

            <center>
                <br />
                <button className="btn bg-gradient-primary w-100" onClick={handleCommit}>
                    Commit Changes
                </button>
                <button className="btn bg-gradient-secondary w-100" onClick={onClose}>
                    Close
                </button>
            </center>
        </div>
    );
};

export default AdvancedSettings;

// non-ai code
//
// import React, {useState} from "react";
// import {_NDNFT} from "../../../abi.js";
// import {UUPSRewardManager} from "../../../abi/UUPSRewardManager.js";
//
// export const AdvancedModal = async (wallet, item, callWrapper, chainData, ethers, toast, smc, ssm, smt) => {
//
//     const abi = _NDNFT;
//
//     let flags;
//     let configs;
//     let rmc;
//
//     let options = {gasLimit: 8000000, value: 0};
//
//     let configAddr;
//
//     async function getFlags(item) {
//         if (wallet) {
//             console.log("aaaa")
//
//             //let cw = new callWrapper("console", {rpcUrl: chainData[1].rpc, chainId: chainData[1].chainId})
//             let signer = (new ethers.providers.Web3Provider(wallet.provider)).getSigner()
//             let ctr = new ethers.Contract(item.NFTProxyAddr ?? item?.contractAddress, abi, signer)
//
//             //let cfg = await ctr.cfg();
//
//
//
//             configAddr = await ctr.NFTConfiguratorAddr();
//
//             await ctr.fe()
//                 .then(async res => {
//                     flags = res;
//                     configs = await ctr.cfg();
//                     rmc = new ethers.Contract(configs.RMAddress, UUPSRewardManager, signer);
//                     console.log("cfg:", configs);
//                     console.log(item.NFTProxyAddr, flags)
//                     //setColls(res)
//                 })
//                 .catch(async e => {
//                     let fallbackAbi = ["function fe() view returns(bool extMintHook,bool bonusERC721,bool rmEnabled,bool rmEnabledStk,bool autoDelegation," +
//                     "bool randomMinting,bool softStaking,bool getExternalPrice,bool preSaleReduction,bool mintDisabled)"]
//                     let signer = (new ethers.providers.Web3Provider(wallet.provider)).getSigner()
//                     let ctr = new ethers.Contract(item.NFTProxyAddr, fallbackAbi, signer)
//                     await ctr.fe()
//                         .then(res => {
//                             flags = res;
//                             console.log(item.NFTProxyAddr, flags)
//                         })
//                         .catch(async (e) => {
//                             let fallbackAbi = ["function fe() view returns(bool extMintHook,bool bonusERC721,bool rmEnabled,bool rmEnabledStk,bool autoDelegation," +
//                             "bool randomMinting,bool softStaking,bool getExternalPrice,bool preSaleReduction)"]
//                             let signer = (new ethers.providers.Web3Provider(wallet.provider)).getSigner()
//                             let ctr = new ethers.Contract(item.NFTProxyAddr, fallbackAbi, signer)
//                             await ctr.fe()
//                                 .then(res => {
//                                     flags = res;
//                                     console.log(item.NFTProxyAddr, flags)
//                                 })
//                                 .catch((e) => {
//                                     console.log(e.message);
//                                 })
//
//                         })
//                 })
//
//         } else {
//             toast("Connect wallet")
//         }
//     }
//
//     await getFlags(item)
//
//     async function setFE(){
//         let cfgabi;
//         let ftot=0;
//         let mul=1;
//         let addArgs = []
//         for(let i=0;i<flags?.length;i++){
//             let tflag=document?.getElementById("f_"+i)
//             console.log(tflag)
//             if(tflag?.checked) ftot+=mul;
//             mul=mul*2
//             if(i===0 && tflag.checked){
//                 let val=document.getElementById("emh_addr").value;
//                 if(val?.length===42){
//                     addArgs.push(["extMintHook", val, 0, ""])
//                 } else {
//                     toast.error("Invalid extMintHook address", {autoClose: false});
//                 }
//             }
//             if(i===1 && tflag.checked){
//                 let val=document.getElementById("be721_addr").value;
//                 if(val?.length===42){
//                     addArgs.push(["bonusERC721", val, 0, ""])
//                 } else {
//                     toast.error("Invalid bonusERC721 address", {autoClose: false});
//                 }
//             }
//             // if(i===2 && tflag.checked){
//             //     let val=document.getElementById("be721_addr").value;
//             //     if(val.length===42){
//             //         addArgs.push(["bonusERC721", val, 0, ""])
//             //     } else {
//             //         toast.error("Invalid bonusERC721 address", {autoClose: false});
//             //     }
//             // }
//             if(i===7 && tflag.checked){
//                 let val=document.getElementById("ep_addr").value;
//                 let fun=document.getElementById("ep_func").value;
//                 if(val?.length===42){
//                     addArgs.push(["externalPrice", val, 0, fun?.length >0 ? fun : "getPrice()"])
//                 } else {
//                     toast.error("Invalid external price address", {autoClose: false});
//                 }
//             }
//             if(i===8 && tflag.checked){
//                 let val=document.getElementById("wl_dcpct").value;
//                 //let fun=document.getElementById("ep_func").value;
//                 if(parseInt(val) <101){
//                     addArgs.push(["WLDiscount", "0x0000000000000000000000000000000000000000", val, ""])
//                 } else {
//                     toast.error("Invalid whitelist discount amount (%)", {autoClose: false});
//                 }
//             }
//             if(i===10 && tflag.checked){
//                 let val=document.getElementById("sr_bips").value;
//                 let num = parseInt(val, 10)
//                 if(!isNaN(num) && num >0 && num <10001 ){
//                     addArgs.push(["splitRoyalty", val, 0, ""])
//                 } else {
//                     toast.error("Invalid split royaty bips amount (0-10000 = 0%-100%)", {autoClose: false});
//                 }
//             }
//         }
//
//
//         try {
//             let val = document.getElementById("ref_bips").value;
//             let num = parseInt(val, 10)
//             if (!isNaN(num) && num > 0 && num < 101) {
//                 addArgs.push(["rPoolPercent", "0x0000000000000000000000000000000000000000", val, ""])
//             } else {
//                 toast.error("Invalid rPool percent (0-100 = 0%-100%)", {autoClose: false});
//             }
//         } catch (e) {
//
//         }
//
//         try {
//             let val = document.getElementById("mlt_bips").value;
//             let num = parseInt(val, 10)
//             if (!isNaN(num) && num >= 0 && num <= 10000) {
//                 await rmc.setMinterCut(num, options)
//                     .then(r=>{
//                         console.log(r)
//                     })
//                     .catch(r=>{
//                         console.log(r)
//                     });
//                 //addArgs.push(["rPoolPercent", "0x0000000000000000000000000000000000000000", val, ""])
//             } else {
//                 toast.error("Minters cut (0-10000 = 0%-100%)", {autoClose: false});
//             }
//         } catch (e) {
//             console.log(e, rmc)
//             toast.error("Call to rewardmanager failed");
//         }
//
//         //toast("CA:" + configAddr)
//
//
//         console.log(addArgs)
//
//
//         let fAbi = [
//             "function SetFlagsUint(address ctr,uint256 flags,bool immediate)",
//             "function advConfig(address ctr,uint256 flags,tuple(string,address,uint256,string)[])",
//             "function Address_RewardManager(address ctr, address rmadr, bool immed)"
//         ]
//         let signer = (new ethers.providers.Web3Provider(wallet.provider)).getSigner()
//         let cctr = new ethers.Contract(configAddr, fAbi, signer)
//         await cctr.advConfig(item.NFTProxyAddr, ftot, addArgs, options)
//             .then(()=>{
//                 toast.success("Changes committed");
//             })
//             .catch((e)=>{
//                 toast.error(<><b>Failed!</b><br/><br/>{e.message}</>);
//             })
//
//
//         let val = document.getElementById("f_2").checked;
//         console.log("VAL: ", val)
//         //let num = parseInt(val, 10)
//         if (val) {
//             await cctr.Address_RewardManager(item.NFTProxyAddr, "0x0000000000000000000000000000000000000000", true, options)
//                 .then(toast(<><b>Success:</b><br/>RewardManager is disabled</>))
//                 .catch(async e => {
//                     await cctr.callStatic.Address_RewardManager(item.NFTProxyAddr, "0x0000000000000000000000000000000000000000", true)
//                         .catch(async em => {
//                             toast(<><b>Error:</b><br/>{em?.reason}</>)
//                         })
//
//                 })
//
//         } else {
//             let RMInput = document.getElementById("rm_address").value;
//             if((configs?.RMAddress !== RMInput && RMInput?.length===42)){
//                 await cctr.Address_RewardManager(item.NFTProxyAddr, RMInput, true, options)
//                     .then(toast(<><b>Success:</b><br/>RewardManager is enabled with new addresss {RMInput}</>))
//                     .catch(async e => {
//                         await cctr.callStatic.Address_RewardManager(item.NFTProxyAddr, RMInput, true)
//                             .catch(async em => {
//                                 toast(<><b>Error:</b><br/>{em?.reason}</>)
//                             })
//
//                     })
//
//             }
//
//             //toast.error("Invalid rPool percent (0-100 = 0%-100%)", {autoClose: false});
//
//         }
//
//         //Address_RewardManager
//
//     }
//
//     function mcu() {
//         let cctr;
//         smc(<>
//             <div style={{minWidth: "98%"}}>
//                 <center>
//                     Advanced config options, please use caution here!<br/>
//                     <a className="text-light" target="_blank"
//                        href="https://f.viridissima.es/d/12-guide-to-nft-deployer-advanced-options">Instructions (links
//                         to Forum)</a>
//                 </center>
//                 <br/>
//
//                 <input defaultChecked={!!flags?.extMintHook}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_0"
//                        onChange={() => {
//                            mcu()
//                        }}
//                 />Enable Mint Hook
//                 <br/>
//                 <input style={document?.getElementById("f_0")?.checked ? {
//                     visibility: "visible",
//                     display: "block"
//                 } : {visibility: "hidden", display: "none"}}
//                        className="cbItem input-purple w-75"
//                        id="emh_addr"
//                        placeholder="ExtMintHook contract address"
//                 />
//
//                 <input defaultChecked={!!flags?.bonusERC721}
//                        className="cbItem" type="checkbox" id="f_1"
//                        onChange={() => {
//                            mcu()
//                        }}
//                 />Enable Bonus ERC721
//                 <br/>
//                 <input style={document?.getElementById("f_1")?.checked ? {
//                     visibility: "visible",
//                     display: "block"
//                 } : {visibility: "hidden", display: "none"}}
//                        className="cbItem input-purple w-75"
//                        id="be721_addr"
//                        placeholder="BonusERC721 contract address"
//                 />
//
//                 <input defaultChecked={!!flags?.rmEnabled}
//                        className="cbItem"
//                        type="checkbox"
//                        onChange={() => {
//                            mcu()
//                        }}
//                        id="f_2"/>Disable RewardManager
//                 <br/>
//                 <input style={document?.getElementById("f_2")?.checked ? {
//                     visibility: "hidden",
//                     display: "none"
//                 } : {visibility: "visible", display: "block"}}
//                        className="cbItem input-purple w-75"
//                        id="rm_address"
//                        placeholder={configs?.RMAddress ? configs.RMAddress : "RM Address"}
//                     //value={s}
//                 />
//
//
//                 <input defaultChecked={!!flags?.autoDelegation}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_4"
//                 />Enable auto FTSO delegation
//                 <br/>
//
//                 <input disabled={!!flags?.randomMinting} defaultChecked={!!flags?.randomMinting}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_5"
//                 />Enable Random Minting
//                 <br/>
//
//                 <input defaultChecked={!!flags?.softStaking}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_6"
//                 />Enable Soft Staking
//                 <br/>
//
//                 <input defaultChecked={!!flags?.rmEnabledStk}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_3"
//                        onChange={() => {
//                            mcu()
//                        }}
//                 />Enable RM While Staking
//                 <br/>
//                 <input style={document?.getElementById("f_3")?.checked ? {
//                     visibility: "visible",
//                     display: "block"
//                 } : {visibility: "hidden", display: "none"}}
//                        className="cbItem input-purple w-75"
//                        id="stake_address"
//                        placeholder="External Staking Contract Addr"
//                 />
//
//                 <input defaultChecked={!!flags?.getExternalPrice} // || !!document.getElementById("f_pp").checked}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_7"
//                        onChange={() => {
//                            mcu()
//                        }}/>Enable Get External Price
//                 <br/>
//                 <input style={document?.getElementById("f_7")?.checked ? {
//                     visibility: "visible",
//                     display: "block"
//                 } : {visibility: "hidden", display: "none"}}
//                        className="cbItem input-purple w-75"
//                        id="ep_addr"
//                        placeholder="External Price Contract Addr"
//                 />
//                 <input style={document?.getElementById("f_7")?.checked ? {
//                     visibility: "visible",
//                     display: "block"
//                 } : {visibility: "hidden", display: "none"}}
//                        className="cbItem input-purple w-75"
//                        id="ep_func"
//                        placeholder="getPrice()"
//                 />
//
//                 <input defaultChecked={!!flags?.getExternalPrice}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_pp"
//                        onChange={() => {
//                            mcu()
//                        }}/>Peg Price to USD
//                 <br/>
//                 <input style={document?.getElementById("f_pp")?.checked ? {
//                     visibility: "visible",
//                     display: "block"
//                 } : {visibility: "hidden", display: "none"}}
//                        className="cbItem input-purple w-75"
//                        id="ep_addr"
//                        placeholder="Mint cost in whole USD, ie $1 or $500"
//                 />
//
//                 <input defaultChecked={!!flags?.preSaleReduction}
//                        className="cbItem" type="checkbox"
//                        id="f_8"
//                        onChange={() => {
//                            mcu()
//                        }}
//                 />Enable Whitelist Discount
//                 <br/>
//                 <input type="number"
//                        style={document?.getElementById("f_8")?.checked ? {visibility: "visible", display: "block"} : {
//                            visibility: "hidden",
//                            display: "none"
//                        }}
//                        className="cbItem input-purple w-75"
//                        id="wl_dcpct"
//                        placeholder="Whitelist discount (percentage)"
//                 />
//
//                 {flags?.length > 8 ? <>
//                     <input defaultChecked={!!flags?.mintDisabled}
//                            className="cbItem"
//                            type="checkbox"
//                            id="f_9"
//                     />Disable Minting
//                     <br/>
//                 </> : null}
//
//                 {flags?.length > 9 ? <>
//                     <input defaultChecked={!!flags?.splitRoyalty}
//                            className="cbItem"
//                            type="checkbox"
//                            id="f_10"
//                            onChange={() => {
//                                mcu()
//                            }}
//                     />Enable Split Royalty
//                     <br/>
//                     <input type="number" style={document?.getElementById("f_10")?.checked ? {
//                         visibility: "visible",
//                         display: "block"
//                     } : {
//                         visibility: "hidden",
//                         display: "none"
//                     }}
//                            className="cbItem input-purple w-75"
//                            id="sr_bips"
//                            placeholder="Split to owner (bips)"
//                     />
//
//                     <input defaultChecked={!!flags?.onChainTraits}
//                            className="cbItem"
//                            type="checkbox"
//                            id="f_11"
//                     />Enable On Chain Traits
//                     <br/>
//
//                 </> : null}
//
//                 <input defaultChecked={!!flags?.splitRoyalty}
//                        className="cbItem"
//                        type="checkbox"
//                        id="f_ref"
//                        onChange={() => {
//                            mcu()
//                        }}
//                 />Enable Mint Reflection
//                 <br/>
//                 <input type="number"
//                        style={document?.getElementById("f_ref")?.checked ? {visibility: "visible", display: "block"} : {
//                            visibility: "hidden",
//                            display: "none"
//                        }}
//                        className="cbItem input-purple w-75"
//                        id="ref_bips"
//                        placeholder="Percentage of mint income to holders"
//                 />
//                 <input defaultChecked={false}
//                        className="cbItem"
//                        type="checkbox"
//                        id="mlt_ref"
//                        onChange={() => {
//                            mcu()
//                        }}
//                 />Minters Lifetime cut
//                 <br/>
//                 <input type="number" style={document?.getElementById("mlt_ref")?.checked ? {
//                     visibility: "visible",
//                     display: "block"
//                 } : {
//                     visibility: "hidden",
//                     display: "none"
//                 }}
//                        className="cbItem input-purple w-75"
//                        id="mlt_bips"
//                        placeholder="Percentage (bips) of rewards to minters"
//                 />
//
//                 <center>
//                     <br/>
//                     <div id="count"></div>
//                     <button className="btn bg-gradient-primary fs-6 fw-bold w-100 mb-1 active"
//                             onClick={() => setFE()}>Commit Changes
//                     </button>
//                     <button className="btn bg-gradient-primary fs-6 fw-bold w-100 mb-1 active"
//                             onClick={() => ssm(false)}>Close
//                     </button>
//                 </center>
//             </div>
//             {/*                </div>
//             </div>*/}
//         </>)
//     }
//
//     mcu();
//     smt("Advanced Config")
//     ssm(true);
//
//     // function taFunc(indata, firstRun, event) {
//     //     let ta = document.getElementById("textarea");
//     //     if (firstRun) ta.value = "";
//     //     if (!indata) {
//     //         indata = ta.value;
//     //     } else {
//     //         indata = ta.value + "\n" + indata;
//     //     }
//     //
//     //     let rep = indata.replace("\r", "\n", -1).replace("\n\n\n", "\n", -1).replace("\n\n", "\n", -1).replace(/[^a-fA-F0-9x\n]/g, "");
//     //     let arr = rep.split("\n");
//     //     let arr2 = [];
//     //     let badct = 0;
//     //     let badlist = [];
//     //     let dupct = 0;
//     //     arr.forEach((r, i) => {
//     //         //console.log(i, r);
//     //         if (r?.length === 42) {
//     //             if (!arr2.includes(r)) {
//     //                 arr2.push(r);
//     //             } else {
//     //                 dupct++;
//     //             }
//     //         } else if (r.length > 0) {
//     //             badct++;
//     //             badlist.push(r);
//     //             console.log("bad:", r);
//     //         }
//     //
//     //     })
//     //     try {
//     //         document.getElementById("count").innerText = arr2.length.toString() + " valid addresses";
//     //     } catch {
//     //     }
//     //     //swl(arr2)
//     //     //delete(arr2[arr2.length-1])
//     //     if (badct > 0) {
//     //         toast.warn(<>
//     //             <div><b>Removed {badct} bad addresses{dupct ? <> and {dupct} duplicates</> : null}:</b></div>
//     //             <div style={{fontSize: "10px", fontFamily: "monospace"}}>{badlist.map(i => <>{i}<br/></>)}</div>
//     //         </>, {autoClose: 15000})
//     //     }
//     //     //+event?.keycode === 13 ? "\n" : "";
//     //     let to = arr2.join("\n");
//     //     if (!to) event?.preventDefault();
//     //     ta.value = to === "\n" ? "" : to;
//     //
//     //     ta = document.getElementById("textarea");
//     //
//     //     heightLimit = 500;
//     //     ta.style.height = ""; /* Reset the height*/
//     //     let m = Math.min(ta.scrollHeight, heightLimit) + "px";
//     //     ta.style.height = m;
//     //     console.log("SH:", ta.scrollHeight, m)
//     //
//     // };
//     // taFunc("", true)
// }
//
//
//
//
